'use client'

import { useEffect, useRef, useState } from 'react'

import carFallbackImage from '../_assets/car-fallback.png'

/** 썸네일 크기 — 데스크탑 100×56.25 / 모바일 72×40.5 (둘 다 16:9 근사, Figma) */
type TRankThumbnailSize = 'sm' | 'lg'

const THUMBNAIL_SIZE_CLASS: Record<TRankThumbnailSize, string> = {
  sm: 'h-[40.5px] w-[72px]',
  lg: 'h-[56.25px] w-[100px]',
}

interface IRankCarThumbnailProps {
  /** 차종 대표 이미지 URL 후보(우선순위 순). 실패 시 다음 후보로 폴백. */
  imageUrls: string[]
  /** 대체 텍스트 (예: '기아 더 뉴 그랜저') */
  alt: string
  /** 크기 — 모바일 리스트는 'sm', 데스크탑 카드는 'lg'(기본) */
  size?: TRankThumbnailSize
}

/**
 * 랭킹 리스트 행 차 썸네일 — Figma 데스크탑 100×56.25 / 모바일 72×40.5 (16:9 근사).
 * 리소스 서버 이미지 키가 차종마다 제각각이라 후보를 순서대로 시도하고,
 * 모두 실패하면 중립 배경으로 폴백한다.
 */
const RankCarThumbnail = ({
  imageUrls,
  alt,
  size = 'lg',
}: IRankCarThumbnailProps) => {
  const [srcIndex, setSrcIndex] = useState(0)
  const imgRef = useRef<HTMLImageElement>(null)
  const currentUrl = imageUrls[srcIndex]

  const handleError = () => setSrcIndex(index => index + 1)

  // SSR HTML의 img는 하이드레이션 전에 error가 지나가 onError를 놓칠 수 있다.
  // 마운트 시 이미 로드 완료(complete)인데 naturalWidth===0(깨짐)이면 폴백 처리.
  useEffect(() => {
    const img = imgRef.current
    if (img && img.complete && img.naturalWidth === 0) handleError()
  }, [currentUrl])

  return (
    <div
      className={`flex shrink-0 items-center overflow-hidden ${THUMBNAIL_SIZE_CLASS[size]}`}
    >
      {currentUrl ? (
        <img
          ref={imgRef}
          src={currentUrl}
          alt={alt}
          className='h-full w-full object-cover'
          onError={handleError}
        />
      ) : (
        // 차종 이미지 없는 경우 폴백 일러스트 (Figma 8877:293206)
        <img
          src={carFallbackImage.src}
          alt={alt}
          className='h-full w-full object-cover'
        />
      )}
    </div>
  )
}

export default RankCarThumbnail
